<template>
    <div>
        <ColumnsSelector
            ref="sent-requests-columns"
            :fields="fields"
            :active-fields="activeFields"
            @activate="activateColumn"
            @inactivate="inactivateColumn"
        />
        <ExternalResponseModal
            ref="externalResponse"
            @saved="processExternalResponse"
        />
        <HamletTable
            ref="hamletTable"
            :columns="activeFields"
            :fetch="fetch"
            :search="true"
            table-container-class="table-responsive hamlet-table product-requests-table"
            @created="onTableCreated"
            @onRowClick="onRowClick"
            @per-page-change="onPerPageChange"
            @page-change="onPageChange"
            :defaultPerPage="savedPerPage"
            :defaultPage="savedPage"
            :default-filters="getDefaultFilters()"
        >
            <template #header-label>
                {{ isFreeSubscriber ? $t('Page(ForwardedRequest):Component(Label):page-label') : $t('Page(SentRequest):Component(Label):page-label') }}
            </template>

            <template #header-controls>
                <div class="text-right d-flex align-items-center justify-content-end">
                    <div class="pr-1">{{ $t('Component(RefreshInterval):label') }}</div>
                    <div class="pr-1">
                        <b-form-select
                            v-model="updateInterval"
                            :options="updateIntervals"
                            @change="onUpdateIntervalChange"
                        />
                    </div>
                    <ColumnsSelectorButton
                        @click="showColumns"
                    />
                </div>
            </template>

            <template #cell-messagingStatus="data">
                <div class="text-center">
                    <span v-if="data.item.messagingStatus !== 'none'">
                        <BIconEnvelope v-if="data.item.messagingStatus === 'sent'" scale="1.5" />
                        <BIconEnvelopeOpen v-else scale="1.5" />
                    </span>
                </div>
            </template>

            <template #cell-status="data">
                <div @click.stop.prevent="onStatusClick(data.item)">
                    <StatusBadge
                        :class="{'text-decoration-underline': needToUnderlineStatus(data.item)}"
                        type="sent"
                        :status="data.item.status"
                    />
                </div>
            </template>

            <!-- A virtual column -->
            <template #cell-sentAt="data">
                {{ data.item.sentAt|moment('Y-MM-DD') }}
            </template>

            <!-- A custom formatted column -->
            <template #cell-deadlineDays="data">
                <div class="text-center">{{ data.item.daysToDeadLine }}</div>
            </template>

            <template #cell-level="data">
                {{ $t('Component(InformationLevelEnum):' + data.item.informationLevel) }}
            </template>

            <template #cell-regulations="data">
                <div :title="getRegulationsTitle(data.item)">
                    <div
                        v-for="(regulation, index) in getVisibleRegulations(data.item)"
                        :key="`${data.item.id}-regulation-${index}`"
                        class="product-requests-regulation"
                    >
                        {{ regulation }}
                    </div>
                    <div v-if="getHiddenRegulationsCount(data.item) > 0" class="product-requests-regulation-more">
                        +{{ getHiddenRegulationsCount(data.item) }}
                    </div>
                </div>
            </template>


            <template #cell-verification="data">
                <b-badge v-for="document in data.item.verificationDocuments" class="badge-glow verification-badge">
                    {{ $t('Component(VerificationDocumentTypeEnum):' + document) }}
                </b-badge>
            </template>

            <template #cell-logged_in="data">
                <div :title="$t('Page(SentRequests):Component(Grid):hover-status-logged-in')">
                    {{ calculateDayDiff(data.item.loggedInAt, data.item) }}
                </div>
            </template>

            <template #cell-opened="data">
                <div :title="$t('Page(SentRequests):Component(Grid):hover-status-opened')">
                    {{ calculateDayDiff(data.item.openedAt, data.item) }}
                </div>
            </template>

            <template #cell-changed="data">
                <div :title="$t('Page(SentRequests):Component(Grid):hover-status-changed')">
                    {{ calculateDayDiff(data.item.updatedAt, data.item) }}
                </div>
            </template>

            <template #cell-replied="data">
                <div :title="$t('Page(SentRequests):Component(Grid):hover-status-replied')">
                    <strong>
                        {{ calculateDayDiff(data.item.repliedAt, data.item) }}
                    </strong>
                </div>
            </template>

            <template #cell-approved="data">
                <div :title="$t('Page(SentRequests):Component(Grid):hover-status-approved')">
                    {{ calculateDayDiff(data.item.approvedAt, data.item) }}
                </div>
            </template>

            <template #cell-productId="data">
                <div :title="getProductIdTitle(data.item)">
                    {{ getTruncatedProductId(data.item) }}
                </div>
            </template>

            <template #cell-productName="data">
                <div :title="getProductNameTitle(data.item)">
                    {{ getTruncatedProductName(data.item) }}
                </div>
            </template>

            <!-- A virtual composite column -->
            <template #cell-supplier="data">
                <div :title="getSupplierTitle(data.item)">
                    {{ getTruncatedSupplierName(data.item) }}
                </div>
            </template>

            <template #filter-cell-status>
                <VueSelectFilter
                    style="min-width: 110px;"
                    :options="statusOptions"
                    :initial-value="filters.status"
                    @filter="filterByStatus"
                />
            </template>

            <template #filter-cell-productId>
                <FilterInput
                    style="min-width: 120px;"
                    :initial-value="filters.productId"
                    @search="filterByProductId"
                />
            </template>

            <template #filter-cell-productName>
                <FilterInput
                    style="min-width: 120px;"
                    :initial-value="filters.productName"
                    @search="filterByProductName"
                />
            </template>

            <template #filter-cell-supplier>
                <AjaxVueSelectFilter
                    ref="supplierFilter"
                    style="min-width: 220px;"
                    :url="allSuppliersEndpoint"
                    :placeholder="$t('Component(Grid):column-supplier')"
                    :initial-value="filters.supplier"
                    @search="onSupplierSearch"
                />
            </template>

            <template #filter-cell-regulations="data">
                <LegislationsFilter
                    style="min-width: 130px;"
                    :initial-value="filters.legislations"
                    @selected="filterLegislations"
                />
            </template>
        </HamletTable>
    </div>
</template>
<script>
import {
    BAlert, BFormSelect, BCardText, BBadge, BTable, BCardTitle, BCard,
    BRow, BCol, BFormGroup, BButton, BCardGroup, VBPopover, VBTooltip, BIconEnvelopeOpen, BIconEnvelope
} from 'bootstrap-vue'
import {ProductApi, SystemApi, UserRoute} from "@core/api";
import {getSupplierName, getSupplierIdentifier, RequestStatus} from "@/libs/product";
import {ColumnsSelectorButton, ColumnsSelector, HamletTable} from "@core/components/common";
import StatusBadge from "@/views/request/components/StatusBadge";
import moment from 'moment'
import activeTableColumns from "@core/mixins/active-table-columns";
import {AjaxVueSelectFilter, FilterInput, LegislationsFilter, VueSelectFilter} from "@core/components/common/filter";
import {ProductBreadcrumbs, sentRequestProvider} from "@core/domain/product";
import ExternalResponseModal from "@/views/request/components/ExternalResponseModal.vue";
import backendResponse from "@core/mixins/ui/backend-response";

let intervalId;
const DEFAULT_PER_PAGE = 30;

function normalizeSentRequestsSettings(settings) {
    if (!settings || typeof settings !== 'object' || Array.isArray(settings)) {
        return {
            filters: {},
            perPage: DEFAULT_PER_PAGE,
            page: 1,
        };
    }

    if (Object.prototype.hasOwnProperty.call(settings, 'filters')) {
        return {
            filters: structuredClone(settings.filters || {}),
            perPage: Number(settings.perPage) || DEFAULT_PER_PAGE,
            page: Number(settings.page) || 1,
        };
    }

    return {
        filters: structuredClone(settings),
        perPage: DEFAULT_PER_PAGE,
        page: 1,
    };
}

export default {
    mixins: [activeTableColumns, backendResponse],
    directives: {
        'b-popover': VBPopover,
        'b-tooltip': VBTooltip,
    },
    components: {
        ExternalResponseModal,
        LegislationsFilter,
        AjaxVueSelectFilter,
        FilterInput,
        VueSelectFilter,
        BCard, BIconEnvelopeOpen, BIconEnvelope, BBadge, BCardText, BButton, BAlert, BRow, BCol, BFormGroup,
        BCardGroup, BTable, BCardTitle, BFormSelect, StatusBadge, HamletTable, ColumnsSelectorButton, ColumnsSelector,
    },
    data() {
        const savedSettings = normalizeSentRequestsSettings(this.$store.getters.runtimeConfig.sentRequestsSettings);

        return {
            filters: savedSettings.filters,
            savedPerPage: savedSettings.perPage,
            savedPage: savedSettings.page,

            statusOptions: RequestStatus.all().map((status) => {
                return {
                    status: status,
                    title: this.$t('Component(Text):status-' + status)
                };
            }),

            allSuppliersEndpoint: UserRoute.supplyChain.allSuppliers(),

            updateInterval: '10000',
            updateIntervals: [
                {text: this.$t('Component(RefreshInterval):do-not-update'), value: ''},
                {text: this.$t('Component(RefreshInterval):interval', {seconds: 10}), value: '10000'},
                {text: this.$t('Component(RefreshInterval):interval', {seconds: 20}), value: '20000'},
                {text: this.$t('Component(RefreshInterval):interval', {seconds: 30}), value: '30000'},
            ],
            intervalId: null,
            activeFields: [],
            fields: [
                {
                    key: 'messagingStatus',
                    label: this.$t('Page(SentRequests):Component(Grid):field-messaging-status'),
                    title: this.$t('Page(SentRequests):Component(Grid):hover-field-messaging-status'),
                    order: 0,
                    active: true,
                },
                {
                    key: 'status',
                    label: this.$t('Page(SentRequests):Component(Grid):field-status'),
                    title: this.$t('Page(SentRequests):Component(Grid):hover-field-status'),
                    order: 0,
                    active: true,
                },
                {
                    key: 'deadlineDays',
                    label: this.$t('Page(SentRequests):Component(Grid):field-deadline-days'),
                    order: 2,
                    active: true,
                    tdClass: 'product-requests-d2d',
                    thClass: 'product-requests-d2d product-requests-pre-line'
                },
                {
                    key: 'productId',
                    label: this.$t('Page(SentRequests):Component(Grid):field-product-id'),
                    title: this.$t('Page(SentRequests):Component(Grid):hover-product-id'),
                    order: 3,
                    active: true,
                },
                {
                    key: 'productName',
                    label: this.$t('Page(SentRequests):Component(Grid):field-product-name'),
                    order: 4,
                    active: true,
                },
                {
                    key: 'supplier',
                    label: this.$t('Page(SentRequests):Component(Grid):field-supplier'),
                    order: 5,
                    active: true,
                },
                {
                    key: 'regulations',
                    label: this.$t('Page(SentRequests):Component(Grid):field-regulations'),
                    order: 6,
                    active: false,
                },
                {
                    key: 'level',
                    label: this.$t('Page(SentRequests):Component(Grid):field-level'),
                    order: 7,
                    active: false,
                },
                {
                    key: 'verification',
                    label: this.$t('Page(SentRequests):Component(Grid):field-verification'),
                    order: 8,
                    active: false,
                },
                {
                    key: 'logged_in',
                    label: this.$t('Page(SentRequests):Component(Grid):field-status-logged-in'),
                    title: this.$t('Page(SentRequests):Component(Grid):hover-status-logged-in'),
                    order: 9,
                    active: true,
                },
                {
                    key: 'opened',
                    label: this.$t('Page(SentRequests):Component(Grid):field-status-opened'),
                    title: this.$t('Page(SentRequests):Component(Grid):hover-status-opened'),
                    order: 10,
                    active: true,
                },
                {
                    key: 'changed',
                    label: this.$t('Page(SentRequests):Component(Grid):field-status-changed'),
                    title: this.$t('Page(SentRequests):Component(Grid):hover-status-changed'),
                    order: 11,
                    active: true,
                },
                {
                    key: 'replied',
                    label: this.$t('Page(SentRequests):Component(Grid):field-status-replied'),
                    title: this.$t('Page(SentRequests):Component(Grid):hover-status-replied'),
                    order: 12,
                    active: true,
                },
                {
                    key: 'approved',
                    label: this.$t('Page(SentRequests):Component(Grid):field-status-approved'),
                    title: this.$t('Page(SentRequests):Component(Grid):hover-status-approved'),
                    order: 13,
                    active: true,
                },
                {
                    key: 'sentAt',
                    label: this.$t('Page(SentRequests):Component(Grid):field-sent-at'),
                    order: 14,
                    active: true,
                },
            ],
            requests: [],
            statuses: RequestStatus,
            config: null,
            fetchCallback: null,
            showLoader: null,
            autoUpdateInited: false,
            externalResponseDefaultEmail: null,
            clickedRequest: null
        };
    },
    methods: {
        getDefaultFilters() {
            const filters = structuredClone(this.filters);

            if (filters.status instanceof Array) {
                filters.status = filters.status.map(s => s.status)
            }

            return filters;
        },

        filterLegislations(legislations) {
            this.filters.legislations = legislations.map(l => l.id);
            this.$refs.hamletTable.filter('legislations', this.filters.legislations);
            this.saveFilter();
        },

        onSupplierSearch(suppliers) {
            this.filters.supplier = suppliers.map(s => s.id);
            this.$refs.hamletTable.filter('supplier', this.filters.supplier);
            this.saveFilter();
        },

        filterByProductId(id) {
            if (id) {
                this.filters.productId = id;
            } else if (this.filters.hasOwnProperty('productId')) {
                delete this.filters.productId;
            }
            this.$refs.hamletTable.filter('productId', id);
            this.saveFilter();
        },

        filterByProductName(name) {
            if (name) {
                this.filters.productName = name;
            } else if (this.filters.hasOwnProperty('productName')) {
                delete this.filters.productName;
            }
            this.$refs.hamletTable.filter('productName', name);
            this.saveFilter();
        },

        filterByStatus(statuses) {
            if (statuses.length > 0) {
                this.filters.status = statuses;
            } else {
                delete this.filters.status;
            }
            this.$refs.hamletTable.filter('status', statuses.map(s => s.status));
            this.saveFilter();
        },

        saveFilter() {
            this.saveSettings();
        },

        saveSettings() {
            this.$store.dispatch('setRuntimeProperty', {
                name: 'sentRequestsSettings',
                value: {
                    filters: this.filters,
                    perPage: this.savedPerPage,
                    page: this.savedPage,
                },
                removeOnOrganizationSwitch: true
            });
        },
        onPerPageChange(perPage) {
            this.savedPerPage = perPage;
            this.saveSettings();
        },
        onPageChange(page) {
            this.savedPage = page;
            this.saveSettings();
        },

        calculateDayDiff(date, item) {
            if (!date) {
                return '-';
            }

            return moment(date).diff(item.sentAt, 'days') + 1;
        },

        showColumns() {
            this.$refs['sent-requests-columns'].show();
        },

        getProductName(sentRequest) {
            return getSupplierName(sentRequest.product) || '';
        },
        getTruncatedProductName(sentRequest, limit = 25) {
            const productName = this.getProductName(sentRequest);

            if (productName.length <= limit) {
                return productName;
            }

            return `${productName.slice(0, limit)}...`;
        },
        getProductNameTitle(sentRequest, limit = 25) {
            const productName = this.getProductName(sentRequest);

            return productName.length > limit ? productName : '';
        },
        getProductId(sentRequest) {
            return getSupplierIdentifier(sentRequest.product) || '';
        },
        getTruncatedProductId(sentRequest, limit = 25) {
            const productId = this.getProductId(sentRequest);

            if (productId.length <= limit) {
                return productId;
            }

            return `${productId.slice(0, limit)}...`;
        },
        getProductIdTitle(sentRequest, limit = 25) {
            const productId = this.getProductId(sentRequest);

            return productId.length > limit ? productId : '';
        },
        getSupplierName(sentRequest) {
            return sentRequest.supplier ? sentRequest.supplier.title || '' : '';
        },
        getTruncatedSupplierName(sentRequest, limit = 25) {
            const supplierName = this.getSupplierName(sentRequest);

            if (supplierName.length <= limit) {
                return supplierName;
            }

            return `${supplierName.slice(0, limit)}...`;
        },
        getSupplierTitle(sentRequest, limit = 25) {
            const supplierName = this.getSupplierName(sentRequest);

            return supplierName.length > limit ? supplierName : '';
        },
        getRegulations(sentRequest) {
            return (sentRequest.legislations || []).map(l => l.title).filter(Boolean);
        },
        getVisibleRegulations(sentRequest, limit = 2) {
            return this.getRegulations(sentRequest).slice(0, limit);
        },
        getHiddenRegulationsCount(sentRequest, limit = 2) {
            return Math.max(this.getRegulations(sentRequest).length - limit, 0);
        },
        getRegulationsTitle(sentRequest) {
            return this.getRegulations(sentRequest).join(', ');
        },
        onRowClick(sentRequest) {
            this.$router.push({name: 'sent-request', params: {id: sentRequest.id, product: sentRequest.product}, props: {}});
        },
        onUpdateIntervalChange(interval) {
            clearInterval(this.intervalId);
            if (interval) {
                this.autoUpdate();
            }
        },
        autoUpdate() {
            if (this.$route.name !== 'sent-requests') {
                clearInterval(intervalId);
                return;
            }
            if (this.updateInterval) {
                clearInterval(intervalId);
                this.intervalId = intervalId = setInterval(() => {
                    if (this.$route.name !== 'sent-requests') {
                        clearInterval(intervalId);
                        return;
                    }
                    if (this.config && this.fetchCallback) {
                        this.fetch(this.config, this.fetchCallback);
                    }
                }, parseInt(this.updateInterval));
            } else {
                clearInterval(intervalId);
            }
        },

        onTableCreated(config, cb, showLoader) {
            this.config = config;
            this.fetchCallback = cb;
            this.showLoader = showLoader;
        },

        fetch(config, cb) {
            this.config = config;
            this.fetchCallback = cb;

            //  reset interval
            this.autoUpdate();

            return ProductApi.getSentRequests(config.filters, {}, config.page, config.perPage).then(r => {
                cb(r.data.totalItems, r.data.items);

                if (!this.autoUpdateInited) {
                    this.autoUpdateInited = true;
                    this.autoUpdate();
                }
            });
        },
        processExternalResponse(response) {
            if (!this.clickedRequest) {
                return;
            }

            if (this.clickedRequest.status === RequestStatus.NEW || this.clickedRequest.status === RequestStatus.EXTERNAL_RESPONSE) {
                (this.showLoader)();
                const sentRequest = sentRequestProvider(this.clickedRequest.id);
                sentRequest
                    .saveExternalResponse(response)
                    .then(() => {
                        return this.fetch(this.config, this.fetchCallback);
                    })
                    .catch(this.handleBackendError);
            }
        },
        needToUnderlineStatus(request) {
            const highlighted = [RequestStatus.NEW, RequestStatus.EXTERNAL_RESPONSE];
            return highlighted.includes(request.status);
        },
        onStatusClick(request) {
            this.clickedRequest = request;
            if (RequestStatus.NEW === request.status) {
                this.$refs.externalResponse.populate({email: this.externalResponseDefaultEmail, date: null});
                //  move to external popup
                this.$refs.externalResponse.show();
            } else if (RequestStatus.EXTERNAL_RESPONSE === request.status) {
                this.$refs.externalResponse.populate({
                    email: request.externalResponse.email || null,
                    date: request.externalResponse.date || null,
                });
                //  update response data
                this.$refs.externalResponse.show();
            }
        },
        resolveDefaultExternalResponseEmail() {
            const sendEmailPreferences = this.$store.getters.sendEmailPreferences || {};
            if (sendEmailPreferences.transportConfiguration && sendEmailPreferences.transportConfiguration.id) {
                SystemApi.email.getConfigurations().then(r => {
                    const item = r.data.find(c => c.id === sendEmailPreferences.transportConfiguration.id);
                    if (item && item.username) {
                        this.externalResponseDefaultEmail = item.username;
                    }
                });
            }
        }
    },
    watch: {
        '$store.state.user.organization': function () {
            if (intervalId) {
                clearInterval(intervalId);
            }
        }
    },
    computed: {
        numberOfItemsToShowFilter() {
            return 10;
        },
        isFreeSubscriber() {
            return this.$store.getters.isFreeSubscriber === true;
        }
    },
    beforeDestroy() {
        if (intervalId) {
            clearInterval(intervalId);
            intervalId = null;
        }
    },
    created() {
        this.initializeTableColumns(this.fields);
        ProductBreadcrumbs.clear();
        ProductBreadcrumbs.push(
            'Sent requests',
            this.$t('Component(Sidebar):sent-requests'),
            () => {
                this.$router.push({name: 'sent-requests'});
            }
        );

        this.resolveDefaultExternalResponseEmail();
    },
}
</script>
<style>
.product-requests-d2d {
    max-width: 130px;
}
.product-requests-table table th,
.product-requests-table table td {
    padding: 0.72rem 1rem;
}
.product-requests-pre-line {
    white-space: pre-line !important;
}
.product-requests-regulation + .product-requests-regulation {
    margin-top: 0.25rem;
}
.product-requests-regulation-more {
    margin-top: 0.25rem;
    color: #6c757d;
}
.verification-badge {
    margin-bottom: 6px;
    margin-right: 6px;
}
</style>
